import { GraduationCapIcon, LegoIcon, TargetIcon } from '@phosphor-icons/react';
import { compareAsc, format, parseISO, subDays } from 'date-fns';

import { ROUTES } from '@shared/config/routes';

import type {
  DashboardSectionKey,
  DashboardSectionResult,
  DashboardTestHistoryEntry,
  DashboardTestKind,
  DashboardTestResult,
} from './types';
import type { DeadlineCardProps } from '../ui/DeadlineCard';
import type { PracticeCardData } from '../ui/PracticeCard';
import type { ScoreCardProps } from '../ui/ScoreCard';
import type { SectionCardProps } from '../ui/SectionCard';

type DashboardDeadlineData = Omit<DeadlineCardProps, 'className'>;
type DashboardScoreCardData = Pick<ScoreCardProps, 'targetScore'>;
type DashboardSectionData = Omit<SectionCardProps, 'className'>;
type DashboardSectionMeta = Omit<DashboardSectionData, 'improveCount' | 'score' | 'trend'>;
type DashboardTestBlueprint = Omit<DashboardTestHistoryEntry, 'date'> & {
  daysAgo: number;
};
type SectionAttemptBlueprint = Omit<DashboardSectionResult, 'percent'>;

const TEST_HISTORY_WINDOW_DAYS = 56;
const SECTION_TREND_POINTS = 12;
const SCORE_MIN = 205;
const SCORE_MAX = 805;
const IMPROVEMENT_THRESHOLD = 70;

const dashboardSectionOrder: DashboardSectionKey[] = ['quant', 'verbal', 'data'];

const dashboardSectionMeta: Record<DashboardSectionKey, DashboardSectionMeta> = {
  quant: {
    title: 'Quantitative Reasoning',
    description:
      'Test your ability to solve math problems, analyze data, and apply logic. Focused on problem-solving skills, not memorizing formulas.',
    color: '#155DFC',
    accent: '#e0e7ff',
    img: '/src/shared/assets/images/q.png',
    url: ROUTES.QUANT,
  },
  verbal: {
    title: 'Verbal Reasoning',
    description:
      'Measure how well you understand written material, evaluate arguments, and draw logical conclusions. It’s all about clarity, logic, and critical reading.',
    color: '#f97316',
    accent: '#ffe3c2',
    img: '/src/shared/assets/images/v.png',
    url: ROUTES.VERBAL,
  },
  data: {
    title: 'Data Insights',
    description:
      'Show how you interpret charts, graphs, and complex information. Combines data literacy, critical thinking, and judgment in real-world scenarios.',
    color: '#ef4444',
    accent: '#ffe1e8',
    img: '/src/shared/assets/images/d.png',
    url: ROUTES.DATA,
  },
};

const getPercent = (correctCount: number, questionCount: number) =>
  questionCount > 0 ? Math.round((correctCount / questionCount) * 100) : 0;

const getMockScoreFromPercent = (percent: number) =>
  Math.round(SCORE_MIN + ((SCORE_MAX - SCORE_MIN) * percent) / 100);

const buildSectionResult = ({
  section,
  questionCount,
  correctCount,
  focusArea,
}: SectionAttemptBlueprint): DashboardSectionResult => ({
  section,
  questionCount,
  correctCount,
  percent: getPercent(correctCount, questionCount),
  focusArea,
});

const buildTestResult = (sectionAttempts: SectionAttemptBlueprint[]): DashboardTestResult => {
  const sectionResults = sectionAttempts.map(buildSectionResult);
  const questionCount = sectionResults.reduce((total, section) => total + section.questionCount, 0);
  const correctCount = sectionResults.reduce((total, section) => total + section.correctCount, 0);
  const percent = getPercent(correctCount, questionCount);

  return {
    point: getMockScoreFromPercent(percent),
    percent,
    questionCount,
    correctCount,
    sectionResults,
  };
};

const createCompletedTest = ({
  daysAgo,
  kind,
  sectionAttempts,
}: {
  daysAgo: number;
  kind: DashboardTestKind;
  sectionAttempts: SectionAttemptBlueprint[];
}): DashboardTestBlueprint => ({
  daysAgo,
  kind,
  status: 'completed',
  sections: [...new Set(sectionAttempts.map((section) => section.section))],
  result: buildTestResult(sectionAttempts),
});

const createPendingTest = ({
  daysAgo,
  kind,
  status,
  sections,
}: {
  daysAgo: number;
  kind: DashboardTestKind;
  status: 'cancelled' | 'active';
  sections: DashboardSectionKey[];
}): DashboardTestBlueprint => ({
  daysAgo,
  kind,
  status,
  sections,
});

const hasResult = (
  test: DashboardTestHistoryEntry
): test is DashboardTestHistoryEntry & { result: DashboardTestResult } => Boolean(test.result);

const testHistoryBlueprint = [
  createCompletedTest({
    daysAgo: 55,
    kind: 'full',
    sectionAttempts: [
      { section: 'quant', questionCount: 21, correctCount: 8 },
      { section: 'verbal', questionCount: 23, correctCount: 9 },
      { section: 'data', questionCount: 20, correctCount: 7 },
    ],
  }),
  createCompletedTest({
    daysAgo: 52,
    kind: 'targeted',
    sectionAttempts: [
      {
        section: 'quant',
        questionCount: 12,
        correctCount: 5,
        focusArea: 'Probability',
      },
    ],
  }),
  createPendingTest({
    daysAgo: 49,
    kind: 'custom',
    status: 'cancelled',
    sections: ['quant', 'verbal'],
  }),
  createCompletedTest({
    daysAgo: 46,
    kind: 'targeted',
    sectionAttempts: [
      {
        section: 'verbal',
        questionCount: 12,
        correctCount: 6,
        focusArea: 'Critical reasoning',
      },
    ],
  }),
  createCompletedTest({
    daysAgo: 43,
    kind: 'custom',
    sectionAttempts: [
      { section: 'quant', questionCount: 18, correctCount: 10 },
      { section: 'data', questionCount: 16, correctCount: 9 },
    ],
  }),
  createCompletedTest({
    daysAgo: 40,
    kind: 'full',
    sectionAttempts: [
      { section: 'quant', questionCount: 21, correctCount: 13 },
      { section: 'verbal', questionCount: 23, correctCount: 12 },
      { section: 'data', questionCount: 20, correctCount: 11 },
    ],
  }),
  createCompletedTest({
    daysAgo: 37,
    kind: 'targeted',
    sectionAttempts: [
      {
        section: 'data',
        questionCount: 12,
        correctCount: 7,
        focusArea: 'Table analysis',
      },
    ],
  }),
  createCompletedTest({
    daysAgo: 34,
    kind: 'custom',
    sectionAttempts: [{ section: 'verbal', questionCount: 20, correctCount: 13 }],
  }),
  createCompletedTest({
    daysAgo: 31,
    kind: 'targeted',
    sectionAttempts: [
      {
        section: 'quant',
        questionCount: 12,
        correctCount: 9,
        focusArea: 'Number properties',
      },
    ],
  }),
  createCompletedTest({
    daysAgo: 28,
    kind: 'full',
    sectionAttempts: [
      { section: 'quant', questionCount: 21, correctCount: 14 },
      { section: 'verbal', questionCount: 23, correctCount: 15 },
      { section: 'data', questionCount: 20, correctCount: 13 },
    ],
  }),
  createPendingTest({
    daysAgo: 25,
    kind: 'custom',
    status: 'cancelled',
    sections: ['data'],
  }),
  createCompletedTest({
    daysAgo: 22,
    kind: 'targeted',
    sectionAttempts: [
      {
        section: 'verbal',
        questionCount: 12,
        correctCount: 9,
        focusArea: 'Reading comprehension',
      },
    ],
  }),
  createCompletedTest({
    daysAgo: 19,
    kind: 'custom',
    sectionAttempts: [
      { section: 'quant', questionCount: 10, correctCount: 8 },
      { section: 'verbal', questionCount: 12, correctCount: 9 },
      { section: 'data', questionCount: 8, correctCount: 6 },
    ],
  }),
  createCompletedTest({
    daysAgo: 17,
    kind: 'targeted',
    sectionAttempts: [
      {
        section: 'data',
        questionCount: 12,
        correctCount: 8,
        focusArea: 'Multi-source reasoning',
      },
    ],
  }),
  createCompletedTest({
    daysAgo: 14,
    kind: 'full',
    sectionAttempts: [
      { section: 'quant', questionCount: 21, correctCount: 17 },
      { section: 'verbal', questionCount: 23, correctCount: 16 },
      { section: 'data', questionCount: 20, correctCount: 15 },
    ],
  }),
  createCompletedTest({
    daysAgo: 11,
    kind: 'targeted',
    sectionAttempts: [
      {
        section: 'quant',
        questionCount: 12,
        correctCount: 10,
        focusArea: 'Algebraic expressions and polynomials',
      },
    ],
  }),
  createCompletedTest({
    daysAgo: 9,
    kind: 'custom',
    sectionAttempts: [
      { section: 'verbal', questionCount: 18, correctCount: 13 },
      { section: 'data', questionCount: 16, correctCount: 12 },
    ],
  }),
  createCompletedTest({
    daysAgo: 8,
    kind: 'full',
    sectionAttempts: [
      { section: 'quant', questionCount: 21, correctCount: 18 },
      { section: 'verbal', questionCount: 23, correctCount: 18 },
      { section: 'data', questionCount: 20, correctCount: 16 },
    ],
  }),
  createCompletedTest({
    daysAgo: 7,
    kind: 'full',
    sectionAttempts: [
      { section: 'quant', questionCount: 21, correctCount: 13 },
      { section: 'verbal', questionCount: 23, correctCount: 11 },
      { section: 'data', questionCount: 20, correctCount: 5 },
    ],
  }),
  createPendingTest({
    daysAgo: 4,
    kind: 'full',
    status: 'cancelled',
    sections: ['data', 'verbal', 'quant'],
  }),
  createCompletedTest({
    daysAgo: 3,
    kind: 'targeted',
    sectionAttempts: [
      {
        section: 'data',
        questionCount: 12,
        correctCount: 9,
        focusArea: 'Data sufficiency',
      },
    ],
  }),
  createCompletedTest({
    daysAgo: 1,
    kind: 'full',
    sectionAttempts: [
      { section: 'quant', questionCount: 21, correctCount: 19 },
      { section: 'verbal', questionCount: 23, correctCount: 18 },
      { section: 'data', questionCount: 20, correctCount: 17 },
    ],
  }),
  createPendingTest({
    daysAgo: 0,
    kind: 'full',
    status: 'active',
    sections: ['quant', 'verbal', 'data'],
  }),
] satisfies DashboardTestBlueprint[];

const buildTestHistory = () => {
  const today = new Date();

  return testHistoryBlueprint.map(({ daysAgo, ...test }) => ({
    ...test,
    date: format(subDays(today, daysAgo), 'yyyy-MM-dd'),
  }));
};

export const buildScoreChartData = (tests: DashboardTestHistoryEntry[]) => {
  const today = new Date();
  const scoreByDate = new Map(tests.map((test) => [test.date, test.result?.point ?? 0]));

  return Array.from({ length: TEST_HISTORY_WINDOW_DAYS }, (_, index) => {
    const date = format(subDays(today, TEST_HISTORY_WINDOW_DAYS - index - 1), 'yyyy-MM-dd');

    return {
      date,
      score: scoreByDate.get(date) ?? 0,
    };
  });
};

export const buildSectionCardData = (
  tests: DashboardTestHistoryEntry[]
): DashboardSectionData[] => {
  const completedTests = tests.filter(hasResult);

  return dashboardSectionOrder.map((section) => {
    const meta = dashboardSectionMeta[section];
    const sectionHistory = completedTests
      .flatMap((test) =>
        test.result.sectionResults
          .filter((result) => result.section === section)
          .map((result) => ({
            ...result,
            date: test.date,
          }))
      )
      .sort((left, right) => compareAsc(parseISO(left.date), parseISO(right.date)));

    const latestResult = sectionHistory[sectionHistory.length - 1];
    const trend = sectionHistory.slice(-SECTION_TREND_POINTS).map((result) => result.percent);
    const latestFocusAreas = new Map<string, number>();

    sectionHistory.forEach((result) => {
      if (result.focusArea) {
        latestFocusAreas.set(result.focusArea, result.percent);
      }
    });

    const improveCount = Array.from(latestFocusAreas.values()).filter(
      (percent) => percent < IMPROVEMENT_THRESHOLD
    ).length;

    return {
      ...meta,
      score: latestResult?.percent ?? 0,
      trend,
      improveCount,
    };
  });
};

const testHistory = buildTestHistory();

export const dashboardMockData = {
  scoreCard: {
    targetScore: 777,
  } satisfies DashboardScoreCardData,
  deadlineCard: {
    remainingDays: 38,
    totalDays: 40,
    dueLabel: '7 Nov',
    dueYear: '2025',
  } satisfies DashboardDeadlineData,
  practices: [
    {
      action: 'full',
      title: 'Start full exam',
      description: 'Real life GMAT Focus simulation: 64 questions, ~2h 15m long.',
      icon: <GraduationCapIcon size={25} weight="duotone" color="#155DFC" />,
    },
    {
      action: 'custom',
      title: 'Create a custom test',
      description: 'Test builder: start with a partial template or build your own.',
      icon: <LegoIcon size={25} weight="duotone" color="#FF8904" />,
    },
    {
      action: 'targeted',
      title: 'Start target practice',
      description: 'Strengthen your knowledge in the areas you’re struggling with.',
      icon: <TargetIcon size={25} weight="duotone" color="#5EA500" />,
    },
  ] satisfies PracticeCardData[],
  testHistory: testHistory satisfies DashboardTestHistoryEntry[],
};
